export const PUBLIC_NAVIGATION_META_NAME = "enkey-public-navigation";

export const DEFAULT_PUBLIC_MENU_ORDER = [
  "company",
  "services",
  "portfolio",
  "tools",
  "support",
  "network",
] as const;

export type PublicMenuKey = (typeof DEFAULT_PUBLIC_MENU_ORDER)[number];

export type PublicNavigationSnapshot = {
  items: PublicMenuKey[];
  updatedAt: string | null;
};

export function normalizePublicMenuOrder(items?: readonly unknown[]) {
  const selected = (items ?? []).filter(
    (item, index, values): item is PublicMenuKey =>
      typeof item === "string" &&
      (DEFAULT_PUBLIC_MENU_ORDER as readonly string[]).includes(item) &&
      values.indexOf(item) === index,
  );
  for (const item of DEFAULT_PUBLIC_MENU_ORDER) {
    if (!selected.includes(item)) selected.push(item);
  }
  return selected;
}

export function readInitialPublicNavigation(
  documentValue:
    | Pick<Document, "querySelector">
    | undefined = typeof document === "undefined" ? undefined : document,
): PublicNavigationSnapshot | null {
  const encoded = documentValue
    ?.querySelector<HTMLMetaElement>(
      `meta[name="${PUBLIC_NAVIGATION_META_NAME}"]`,
    )
    ?.getAttribute("content");
  if (!encoded) return null;
  try {
    const snapshot = JSON.parse(
      decodeURIComponent(encoded),
    ) as Partial<PublicNavigationSnapshot>;
    return {
      items: normalizePublicMenuOrder(snapshot.items),
      updatedAt:
        typeof snapshot.updatedAt === "string" ? snapshot.updatedAt : null,
    };
  } catch {
    return null;
  }
}

export function resolvePublicNavigation(
  initial: PublicNavigationSnapshot | null,
  live: Partial<PublicNavigationSnapshot> | null | undefined,
): PublicNavigationSnapshot {
  if (!live) {
    return (
      initial ?? {
        items: [...DEFAULT_PUBLIC_MENU_ORDER],
        updatedAt: null,
      }
    );
  }
  return {
    items: normalizePublicMenuOrder(live.items ?? initial?.items),
    updatedAt:
      typeof live.updatedAt === "string"
        ? live.updatedAt
        : (initial?.updatedAt ?? null),
  };
}
